import React, { useMemo, useRef, useState } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import {
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Download,
  FileImage,
  FileText,
  Loader2,
} from "lucide-react";
import { Dialog, DialogContent } from "../../../../../components/ui/dialog";
import {
  downloadSvg,
  downloadPng,
  downloadPdf,
  generateDiagramFilename,
} from "../../../utils/diagramExport";
import { reportMermaidFailure } from "../../../utils/mermaidReporting";

const TOOLBAR_HEIGHT_PX = 45;

/**
 * Mermaid SVGs use width="100%" which makes them fill whatever container
 * they're in, breaking zoom-pan-pinch's ability to calculate scale.
 * This replaces width/height with the viewBox's natural pixel dimensions
 * so the SVG has a fixed intrinsic size.
 */
function normalizeSvg(svgString: string): {
  svg: string;
  width: number;
  height: number;
} {
  const viewBoxMatch = svgString.match(
    /viewBox=["'][\s]*(\S+)\s+(\S+)\s+(\S+)\s+(\S+)["']/
  );
  if (!viewBoxMatch) return { svg: svgString, width: 0, height: 0 };

  const w = parseFloat(viewBoxMatch[3]);
  const h = parseFloat(viewBoxMatch[4]);
  if (w <= 0 || h <= 0) return { svg: svgString, width: 0, height: 0 };

  // Replace width="100%" / height="..." with explicit pixel values
  let normalized = svgString
    .replace(/(<svg[^>]*)\bwidth=["'][^"']*["']/, `$1width="${w}"`)
    .replace(/(<svg[^>]*)\bheight=["'][^"']*["']/, `$1height="${h}"`);

  // Also strip max-width from inline style which Mermaid adds
  normalized = normalized.replace(
    /(<svg[^>]*style=["'][^"']*)max-width:\s*[^;"']+;?/,
    "$1"
  );

  return { svg: normalized, width: w, height: h };
}

interface MermaidDiagramModalProps {
  svgString: string;
  isOpen: boolean;
  onClose: () => void;
  clientInitials?: string;
}

export default function MermaidDiagramModal({
  svgString,
  isOpen,
  onClose,
  clientInitials,
}: MermaidDiagramModalProps) {
  const diagramRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);

  const {
    svg: normalizedSvg,
    initialScale,
    initialX,
    initialY,
  } = useMemo(() => {
    const { svg, width, height } = normalizeSvg(svgString);
    if (width === 0 || height === 0)
      return { svg, initialScale: 1, initialX: 0, initialY: 0 };

    const containerW = window.innerWidth * 0.95;
    const containerH = window.innerHeight * 0.95 - TOOLBAR_HEIGHT_PX;
    const scale = Math.min(containerW / width, containerH / height);

    return {
      svg,
      initialScale: scale,
      initialX: (containerW - width * scale) / 2,
      initialY: (containerH - height * scale) / 2,
    };
  }, [svgString]);

  const handleExport = async (fn: () => Promise<void>) => {
    setExporting(true);
    try {
      await fn();
    } catch (error) {
      reportMermaidFailure("download", "capture", {
        errorName: error instanceof Error ? error.name : typeof error,
      });
      console.error("Failed to export mermaid diagram:", error);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-w-[95vw] max-h-[95vh] w-[95vw] h-[95vh] p-0 flex flex-col"
        description="Fullscreen Mermaid diagram viewer"
        hideCloseButton
        onClose={onClose}
      >
        <TransformWrapper
          initialScale={initialScale}
          initialPositionX={initialX}
          initialPositionY={initialY}
          minScale={0.1}
          maxScale={20}
          limitToBounds={false}
        >
          {({ zoomIn, zoomOut, setTransform }) => (
            <>
              {/* Toolbar */}
              <div className="flex items-center gap-1 px-4 py-2 border-b border-slate-200 bg-white shrink-0">
                <ToolbarButton
                  icon={<ZoomIn className="h-4 w-4" />}
                  title="Zoom in"
                  onClick={() => zoomIn()}
                />
                <ToolbarButton
                  icon={<ZoomOut className="h-4 w-4" />}
                  title="Zoom out"
                  onClick={() => zoomOut()}
                />
                <ToolbarButton
                  icon={<RotateCcw className="h-4 w-4" />}
                  title="Reset zoom"
                  onClick={() => setTransform(initialX, initialY, initialScale)}
                />
                <div className="w-px h-5 bg-slate-200 mx-1" />
                <ToolbarButton
                  icon={<Download className="h-4 w-4" />}
                  title="Download SVG"
                  onClick={() =>
                    downloadSvg(
                      svgString,
                      generateDiagramFilename("svg", clientInitials)
                    )
                  }
                />
                <ToolbarButton
                  icon={<FileImage className="h-4 w-4" />}
                  title="Download PNG"
                  disabled={exporting}
                  onClick={() =>
                    diagramRef.current &&
                    handleExport(() =>
                      downloadPng(
                        diagramRef.current!,
                        generateDiagramFilename("png", clientInitials)
                      )
                    )
                  }
                />
                <ToolbarButton
                  icon={
                    exporting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <FileText className="h-4 w-4" />
                    )
                  }
                  title="Download PDF"
                  disabled={exporting}
                  onClick={() =>
                    diagramRef.current &&
                    handleExport(() =>
                      downloadPdf(
                        diagramRef.current!,
                        generateDiagramFilename("pdf", clientInitials)
                      )
                    )
                  }
                />
                <div className="flex-1" />
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 transition-colors"
                >
                  Close
                </button>
              </div>
              {/* Diagram area */}
              <div className="flex-1 bg-slate-50">
                <TransformComponent
                  wrapperStyle={{ width: "100%", height: "100%" }}
                >
                  <div
                    ref={diagramRef}
                    dangerouslySetInnerHTML={{ __html: normalizedSvg }}
                  />
                </TransformComponent>
              </div>
            </>
          )}
        </TransformWrapper>
      </DialogContent>
    </Dialog>
  );
}

function ToolbarButton({
  icon,
  title,
  onClick,
  disabled = false,
}: {
  icon: React.ReactNode;
  title: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled}
      className="rounded-md p-1.5 text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors disabled:opacity-40 disabled:pointer-events-none"
    >
      {icon}
    </button>
  );
}
